(() => {
  const scriptKey = 'BalourPreviewVaultScriptLoaded';

  if (window[scriptKey]) return;
  window[scriptKey] = true;

  const rootSelector = '[data-bpv-root]';
  const hypeStoragePrefix = 'balour-drop-hype-v1:';
  const viewStoragePrefix = 'balour-drop-view-v1:';
  const visitorStorageKey = 'balour-drop-visitor-v1';
  const viewWindow = 24 * 60 * 60 * 1000;
  const hypeFxDuration = 10000;

  const parseCountdownTarget = (rawTarget) => {
    const target = String(rawTarget || '').trim();
    if (!target) return Number.NaN;

    if (/^\d{10,13}$/.test(target)) {
      const epoch = Number(target);
      return target.length === 10 ? epoch * 1000 : epoch;
    }

    const legacyDate = target.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2}|\d{4})$/);
    if (legacyDate) {
      const month = Number(legacyDate[1]);
      const day = Number(legacyDate[2]);
      const shortYear = Number(legacyDate[3]);
      const year = legacyDate[3].length === 2 ? 2000 + shortYear : shortYear;
      const parsedLegacy = new Date(year, month - 1, day, 0, 0, 0, 0);

      if (
        parsedLegacy.getFullYear() === year
        && parsedLegacy.getMonth() === month - 1
        && parsedLegacy.getDate() === day
      ) {
        return parsedLegacy.getTime();
      }

      return Number.NaN;
    }

    const parsed = Date.parse(target);
    return Number.isFinite(parsed) ? parsed : Number.NaN;
  };

  const updateBodyLock = () => {
    const openVaultDialog = document.querySelector('[data-bpv-modal][open]');
    document.body.classList.toggle('bpv-dialog-open', Boolean(openVaultDialog));
  };

  class BalourPreviewVault {
    constructor(root) {
      this.root = root;
      this.modal = root.querySelector('[data-bpv-modal]');
      this.mount = root.querySelector('[data-bpv-modal-mount]');
      this.activeTrigger = null;
      this.observer = null;
      this.fallbackIsolation = [];
      this.countdowns = [];
      this.countdownTimer = null;
      this.memoryHype = new Set();
      this.memoryViews = new Map();
      this.engagementEndpoint = this.resolveEngagementEndpoint(root.dataset.engagementEndpoint);
      this.hypeSoundEnabled = root.dataset.hypeSound !== 'false';
      this.visitorToken = this.getVisitorToken();
      this.sharedCounts = new Map();
      this.pendingHype = new Set();
      this.engagementGenerations = new Map();
      this.engagementRequests = new Set();
      this.destroyed = false;
      this.hypeFx = null;
      this.hypeFxGeneration = 0;
      this.hypeFxRaf = null;
      this.hypeFxTimer = null;
      this.hypeFxResize = null;
      this.audioContext = null;
      this.hypeAudioNodes = [];
      this.hypeAudioTimer = null;

      this.handleRootClick = this.handleRootClick.bind(this);
      this.handleModalClick = this.handleModalClick.bind(this);
      this.handleModalClose = this.handleModalClose.bind(this);
      this.handleModalKeydown = this.handleModalKeydown.bind(this);
      this.handleStorageChange = this.handleStorageChange.bind(this);

      this.root.addEventListener('click', this.handleRootClick);
      window.addEventListener('storage', this.handleStorageChange);

      if (this.modal) {
        this.modal.addEventListener('click', this.handleModalClick);
        this.modal.addEventListener('close', this.handleModalClose);
        this.modal.addEventListener('keydown', this.handleModalKeydown);
      }

      this.prepareCardReveals();
      this.refreshCountdowns();
      this.syncHypeButtons();
      this.syncEngagementCounts();
      this.loadSharedCounts();
      this.focusFormConfirmation();
    }

    resolveEngagementEndpoint(rawEndpoint) {
      const endpoint = String(rawEndpoint || '').trim();
      if (!endpoint) return '';

      try {
        const url = new URL(endpoint, window.location.origin);
        if (url.origin !== window.location.origin || !url.pathname.startsWith('/apps/')) return '';
        return `${url.origin}${url.pathname.replace(/\/+$/, '')}`;
      } catch (error) {
        return '';
      }
    }

    getVisitorToken() {
      const stored = this.getStoredValue(visitorStorageKey);
      if (stored && /^[a-zA-Z0-9_-]{12,100}$/.test(stored)) return stored;

      let token = '';
      try {
        token = window.crypto?.randomUUID?.() || '';
        if (!token && window.crypto?.getRandomValues) {
          const values = new Uint32Array(4);
          window.crypto.getRandomValues(values);
          token = [...values].map((value) => value.toString(36)).join('-');
        }
      } catch (error) {
        token = '';
      }

      if (!token) {
        token = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
      }

      this.setStoredValue(visitorStorageKey, token);
      return token;
    }

    createEventId() {
      try {
        const id = window.crypto?.randomUUID?.();
        if (id) return id;
      } catch (error) {
        // A timestamp fallback still gives the backend an idempotency key.
      }

      return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    }

    refreshCountdowns() {
      this.countdowns = [...this.root.querySelectorAll('[data-bpv-countdown]')];
      this.updateCountdowns();

      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }

      if (this.countdowns.some((countdown) => this.getCountdownDifference(countdown) > 0)) {
        this.countdownTimer = window.setInterval(() => this.updateCountdowns(), 1000);
      }
    }

    getCountdownDifference(countdown) {
      const target = parseCountdownTarget(countdown.dataset.target);
      return Number.isFinite(target) ? target - Date.now() : Number.NaN;
    }

    updateCountdowns() {
      let hasActiveCountdown = false;

      this.countdowns.forEach((countdown) => {
        const difference = this.getCountdownDifference(countdown);
        const units = countdown.querySelector('[data-countdown-units]');
        const complete = countdown.querySelector('[data-countdown-complete]');

        if (!Number.isFinite(difference)) {
          countdown.hidden = true;
          return;
        }

        countdown.hidden = false;

        if (difference <= 0) {
          countdown.classList.add('is-complete');
          if (units) units.hidden = true;
          if (complete) {
            complete.hidden = false;
            complete.textContent = countdown.dataset.complete || 'Drop is live';
          }
          return;
        }

        hasActiveCountdown = true;
        countdown.classList.remove('is-complete');
        if (units) units.hidden = false;
        if (complete) complete.hidden = true;

        const days = Math.floor(difference / 86400000);
        const hours = Math.floor((difference % 86400000) / 3600000);
        const minutes = Math.floor((difference % 3600000) / 60000);
        const seconds = Math.floor((difference % 60000) / 1000);

        const setValue = (selector, value) => {
          const element = countdown.querySelector(selector);
          if (element) element.textContent = String(value).padStart(2, '0');
        };

        setValue('[data-countdown-days]', days);
        setValue('[data-countdown-hours]', hours);
        setValue('[data-countdown-minutes]', minutes);
        setValue('[data-countdown-seconds]', seconds);
      });

      if (!hasActiveCountdown && this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    }

    prepareCardReveals() {
      const cards = [...this.root.querySelectorAll('.bpv-card')];
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.root.classList.add('bpv--js');

      if (reduceMotion || !('IntersectionObserver' in window)) {
        cards.forEach((card) => card.classList.add('is-visible'));
        return;
      }

      cards.forEach((card, index) => {
        card.style.transitionDelay = `${Math.min(index % 6, 5) * 65}ms`;
      });

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            this.observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -6% 0px', threshold: 0.08 }
      );

      cards.forEach((card) => this.observer.observe(card));
    }

    focusFormConfirmation() {
      const confirmation = this.root.querySelector('.bpv-signup__message--success[tabindex="-1"]');
      if (confirmation) confirmation.focus({ preventScroll: true });
    }

    getStoredValue(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    }

    setStoredValue(key, value) {
      try {
        window.localStorage.setItem(key, value);
        return true;
      } catch (error) {
        return false;
      }
    }

    removeStoredValue(key) {
      try {
        window.localStorage.removeItem(key);
        return true;
      } catch (error) {
        return false;
      }
    }

    getEngagementKeys() {
      const keys = new Set();

      this.root.querySelectorAll('[data-engagement-key], [data-hype-key]').forEach((element) => {
        const key = element.dataset.engagementKey || element.dataset.hypeKey;
        if (this.isValidEngagementKey(key)) keys.add(key);
      });

      return [...keys];
    }

    isValidEngagementKey(key) {
      return /^[a-zA-Z0-9_-]{1,80}$/.test(String(key || ''));
    }

    bumpEngagementGeneration(key) {
      const generation = (this.engagementGenerations.get(key) || 0) + 1;
      this.engagementGenerations.set(key, generation);
      return generation;
    }

    formatCount(value) {
      const count = Math.max(0, Math.floor(Number(value) || 0));

      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          notation: count >= 10000 ? 'compact' : 'standard',
          maximumFractionDigits: count >= 10000 ? 1 : 0
        }).format(count);
      } catch (error) {
        return String(count);
      }
    }

    normalizeCount(value) {
      const count = Number(value);
      return Number.isSafeInteger(count) && count >= 0 && count <= 999999999 ? count : null;
    }

    normalizeEngagementItem(item) {
      if (!item || typeof item !== 'object') return null;

      const views = this.normalizeCount(item.views ?? item.view_count);
      const hypes = this.normalizeCount(item.hypes ?? item.hype_count);
      const normalized = {};

      if (views !== null) normalized.views = views;
      if (hypes !== null) normalized.hypes = hypes;
      if (typeof item.viewer_hyped === 'boolean') normalized.viewerHyped = item.viewer_hyped;
      if (Number.isSafeInteger(item.revision) && item.revision >= 0) normalized.revision = item.revision;

      return Object.keys(normalized).length ? normalized : null;
    }

    applyCountsPayload(
      payload,
      fallbackKey = '',
      applyPendingViewerState = false,
      generationSnapshot = null
    ) {
      const appliedKeys = new Set();
      if (!payload || typeof payload !== 'object') return appliedKeys;

      const updates = [];
      const configuredKeys = new Set(this.getEngagementKeys());
      const source = payload.items ?? payload.counts;

      if (Array.isArray(source)) {
        source.forEach((item) => {
          const key = String(item?.item_key || item?.key || '').trim();
          if (key) updates.push([key, item]);
        });
      } else if (source && typeof source === 'object') {
        Object.entries(source).forEach(([key, item]) => updates.push([key, item]));
      } else {
        const item = payload.item && typeof payload.item === 'object' ? payload.item : payload;
        const key = String(item.item_key || item.key || fallbackKey || '').trim();
        if (key) updates.push([key, item]);
      }

      updates.slice(0, configuredKeys.size || 24).forEach(([key, item]) => {
        if (!configuredKeys.has(key) || !this.isValidEngagementKey(key)) return;
        if (
          generationSnapshot?.has(key)
          && generationSnapshot.get(key) !== (this.engagementGenerations.get(key) || 0)
        ) return;

        const normalized = this.normalizeEngagementItem(item);
        if (!normalized) return;

        const previous = this.sharedCounts.get(key) || {};
        if (
          Number.isSafeInteger(previous.revision)
          && (!Number.isSafeInteger(normalized.revision) || normalized.revision < previous.revision)
        ) return;

        const next = { ...previous, ...normalized };
        this.sharedCounts.set(key, next);
        appliedKeys.add(key);

        if (
          typeof normalized.viewerHyped === 'boolean'
          && (applyPendingViewerState || !this.pendingHype.has(key))
        ) {
          this.setHyped(key, normalized.viewerHyped);
        }
      });

      this.syncHypeButtons();
      this.syncEngagementCounts();
      return appliedKeys;
    }

    syncEngagementCounts(onlyKey = null) {
      this.root.querySelectorAll('[data-bpv-view-metric]').forEach((metric) => {
        const key = metric.dataset.engagementKey;
        if (!key || (onlyKey && key !== onlyKey)) return;

        const countElement = metric.querySelector('[data-bpv-view-count]');
        const views = this.sharedCounts.get(key)?.views;

        if (countElement && Number.isFinite(views)) {
          countElement.textContent = this.formatCount(views);
          countElement.hidden = false;
          metric.setAttribute('aria-label', `${views.toLocaleString()} preview views`);
        } else if (countElement) {
          countElement.hidden = true;
          countElement.textContent = '';
          metric.setAttribute('aria-label', 'Preview views');
        }
      });

      this.root.querySelectorAll('[data-bpv-hype]').forEach((button) => {
        const key = button.dataset.hypeKey;
        if (!key || (onlyKey && key !== onlyKey)) return;

        const countElement = button.querySelector('[data-bpv-hype-count]');
        const hypes = this.sharedCounts.get(key)?.hypes;
        const itemName = button.dataset.hypeName || 'this drop';

        if (countElement && Number.isFinite(hypes)) {
          countElement.textContent = this.formatCount(hypes);
          countElement.hidden = false;
          button.setAttribute('aria-label', `Hype ${itemName}. ${hypes.toLocaleString()} total hypes`);
        } else if (countElement) {
          countElement.hidden = true;
          countElement.textContent = '';
          button.setAttribute('aria-label', `Hype ${itemName}`);
        }
      });

      const activeEffectKey = this.hypeFx?.dataset.hypeKey;
      if (activeEffectKey && (!onlyKey || activeEffectKey === onlyKey)) {
        this.updateHypeFxCount(activeEffectKey);
      }
    }

    async requestEngagement(path, { method = 'GET', params = {}, body = null } = {}) {
      if (!this.engagementEndpoint) return null;

      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort('timeout'), 8000);
      this.engagementRequests.add(controller);

      try {
        const url = new URL(`${this.engagementEndpoint}/${String(path).replace(/^\/+/, '')}`);
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            url.searchParams.set(key, String(value));
          }
        });

        const options = {
          method,
          cache: 'no-store',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: controller.signal
        };

        if (body) {
          options.headers['Content-Type'] = 'application/json';
          options.body = JSON.stringify(body);
        }

        const response = await fetch(url.toString(), options);
        if (!response.ok) throw new Error(`Engagement request failed: ${response.status}`);
        if (response.status === 204) return {};
        const responseText = await response.text();
        return responseText ? JSON.parse(responseText) : {};
      } finally {
        clearTimeout(timeout);
        this.engagementRequests.delete(controller);
      }
    }

    async loadSharedCounts(requestedKeys = null) {
      const availableKeys = new Set(this.getEngagementKeys());
      const keys = (requestedKeys || [...availableKeys]).filter((key) => availableKeys.has(key));
      if (!this.engagementEndpoint || !keys.length) return;
      const generationSnapshot = new Map(
        keys.map((key) => [key, this.engagementGenerations.get(key) || 0])
      );

      try {
        const payload = await this.requestEngagement('counts', {
          params: {
            keys: keys.join(','),
            visitor_token: this.visitorToken
          }
        });
        return this.applyCountsPayload(payload, '', false, generationSnapshot);
      } catch (error) {
        // Totals stay hidden unless an authoritative response is available.
        return new Set();
      }
    }

    isHyped(key) {
      const stored = this.getStoredValue(`${hypeStoragePrefix}${key}`);
      if (stored !== null) return stored === '1';
      return this.memoryHype.has(key);
    }

    setHyped(key, active) {
      if (active) {
        const stored = this.setStoredValue(`${hypeStoragePrefix}${key}`, '1');
        if (stored) {
          this.memoryHype.delete(key);
        } else {
          this.memoryHype.add(key);
        }
        return;
      }

      this.memoryHype.delete(key);
      this.removeStoredValue(`${hypeStoragePrefix}${key}`);
    }

    syncHypeButtons(onlyKey = null) {
      const hypeLabel = this.root.dataset.hypeLabel || 'Hype this drop';
      const hypedLabel = this.root.dataset.hypedLabel || 'Hyped';

      this.root.querySelectorAll('[data-bpv-hype]').forEach((button) => {
        const key = button.dataset.hypeKey;
        if (!key || (onlyKey && key !== onlyKey)) return;

        const active = this.isHyped(key);
        const itemName = button.dataset.hypeName || 'this drop';
        const label = button.querySelector('[data-bpv-hype-label]');

        button.classList.toggle('is-hyped', active);
        button.setAttribute('aria-pressed', String(active));
        button.setAttribute('aria-label', `Hype ${itemName}`);
        if (label) label.textContent = active ? hypedLabel : hypeLabel;
      });

      this.syncEngagementCounts(onlyKey);
    }

    announceHype(button, active) {
      const status = this.root.querySelector('[data-bpv-hype-announcer]');
      if (!status) return;

      const name = button.dataset.hypeName || 'This drop';
      status.textContent = active
        ? `${name} is hyped. No sign-in was required.`
        : `Hype removed from ${name}.`;
    }

    celebrateHype(button, key) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!reduceMotion) {
        button.classList.remove('is-celebrating');
        button.querySelectorAll('.bpv-hype__spark').forEach((spark) => spark.remove());
        void button.offsetWidth;
        button.classList.add('is-celebrating');

        const colors = ['var(--bpv-accent)', '#fff4bf', '#ffffff'];
        const sparkCount = 10;

        for (let index = 0; index < sparkCount; index += 1) {
          const angle = (Math.PI * 2 * index) / sparkCount - Math.PI / 2;
          const distance = 34 + (index % 3) * 9;
          const spark = document.createElement('span');
          spark.className = 'bpv-hype__spark';
          spark.setAttribute('aria-hidden', 'true');
          spark.style.setProperty('--bpv-spark-x', `${Math.cos(angle) * distance}px`);
          spark.style.setProperty('--bpv-spark-y', `${Math.sin(angle) * distance}px`);
          spark.style.setProperty('--bpv-spark-delay', `${(index % 2) * 24}ms`);
          spark.style.setProperty('--bpv-spark-color', colors[index % colors.length]);
          button.appendChild(spark);
        }

        window.setTimeout(() => {
          button.classList.remove('is-celebrating');
          button.querySelectorAll('.bpv-hype__spark').forEach((spark) => spark.remove());
        }, 900);
      }

      if (!window.matchMedia('(forced-colors: active)').matches) {
        this.startHypeExperience(key, button.dataset.hypeName || 'Balour drop', reduceMotion);
      }
    }

    startHypeExperience(key, itemName, reduceMotion) {
      this.stopHypeExperience();
      const experienceGeneration = this.hypeFxGeneration;

      const effect = document.createElement('div');
      effect.className = 'bpv-hype-fx';
      effect.setAttribute('popover', 'manual');
      effect.setAttribute('aria-hidden', 'true');
      effect.dataset.hypeKey = key;

      const canvas = document.createElement('canvas');
      canvas.className = 'bpv-hype-fx__canvas';
      canvas.setAttribute('aria-hidden', 'true');

      const badge = document.createElement('div');
      badge.className = 'bpv-hype-fx__badge';

      const eyebrow = document.createElement('span');
      eyebrow.textContent = 'Community signal';
      const title = document.createElement('strong');
      title.textContent = 'Hype locked in';
      const context = document.createElement('small');
      context.textContent = itemName;
      const count = document.createElement('small');
      count.className = 'bpv-hype-fx__count';
      count.hidden = true;

      badge.append(eyebrow, title, context, count);
      effect.append(canvas, badge);
      const fallbackModal = this.isModalOpen() ? this.modal : null;
      const supportsPopover = typeof effect.showPopover === 'function';
      (supportsPopover ? document.body : (fallbackModal || document.body)).appendChild(effect);
      this.hypeFx = effect;

      if (supportsPopover) {
        try {
          effect.showPopover();
        } catch (error) {
          effect.removeAttribute('popover');
          if (fallbackModal) fallbackModal.appendChild(effect);
        }
      } else {
        effect.removeAttribute('popover');
      }

      if (!supportsPopover && fallbackModal) fallbackModal.classList.add('bpv-modal--hype-fx');
      if (supportsPopover && !effect.hasAttribute('popover') && fallbackModal) {
        fallbackModal.classList.add('bpv-modal--hype-fx');
      }

      this.updateHypeFxCount(key);

      if (!reduceMotion) this.runFireworks(canvas, hypeFxDuration);
      if (this.hypeSoundEnabled) this.playHypeSound(reduceMotion, experienceGeneration);

      const duration = reduceMotion ? 1400 : hypeFxDuration + 1600;
      this.hypeFxTimer = window.setTimeout(() => this.stopHypeExperience(), duration);
    }

    updateHypeFxCount(key) {
      if (!this.hypeFx || this.hypeFx.dataset.hypeKey !== key) return;

      const element = this.hypeFx.querySelector('.bpv-hype-fx__count');
      const hypes = this.sharedCounts.get(key)?.hypes;
      if (!element) return;

      if (Number.isFinite(hypes)) {
        element.textContent = `${this.formatCount(hypes)} total hypes`;
        element.hidden = false;
      } else {
        element.textContent = '';
        element.hidden = true;
      }
    }

    runFireworks(canvas, duration) {
      const context = canvas.getContext('2d', { alpha: true });
      if (!context) return;

      const rockets = [];
      const particles = [];
      const colors = ['#fff4b8', '#d4af37', '#ffffff', '#8ed8ff', '#ff9f43', '#ff5fa2'];
      const mobile = window.matchMedia('(max-width: 639px)').matches;
      const particleCount = mobile ? 34 : 52;
      const maximumParticles = mobile ? 440 : 760;
      const launchTimes = mobile
        ? [0, 520, 1080, 1660, 2260, 2860, 3480, 4120, 4760, 5400, 6040, 6680, 7300, 7860, 8340, 8720, 9020]
        : [0, 320, 660, 1020, 1400, 1800, 2220, 2660, 3120, 3580, 4060, 4540, 5020, 5500, 5980, 6460, 6940, 7380, 7780, 8140, 8460, 8740, 8980, 9200];
      const launchSources = ['bottom', 'left', 'right', 'bottom-left', 'bottom-right', 'top-left', 'top-right', 'left', 'right', 'bottom', 'top'];
      const launchScales = [0.68, 1, 1.42, 0.78, 1.18, 0.62, 1.5, 0.88, 1.28, 0.72, 1.08];
      const startedAt = performance.now();
      let width = 0;
      let height = 0;
      let pixelRatio = 1;
      let lastFrame = startedAt;
      let launchIndex = 0;

      const resize = () => {
        width = Math.max(1, window.innerWidth);
        height = Math.max(1, window.innerHeight);
        const desiredRatio = Math.min(window.devicePixelRatio || 1, 2);
        const ratioLimit = Math.sqrt(3000000 / (width * height));
        pixelRatio = Math.max(0.5, Math.min(desiredRatio, ratioLimit));
        canvas.width = Math.floor(width * pixelRatio);
        canvas.height = Math.floor(height * pixelRatio);
        context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      };

      const launchRocket = (source, scale, finale = false) => {
        let launchX = width * (0.15 + Math.random() * 0.7);
        let launchY = height + 24;

        if (source === 'left') {
          launchX = -24;
          launchY = height * (0.22 + Math.random() * 0.64);
        } else if (source === 'right') {
          launchX = width + 24;
          launchY = height * (0.22 + Math.random() * 0.64);
        } else if (source === 'top') {
          launchX = width * (0.12 + Math.random() * 0.76);
          launchY = -24;
        } else if (source === 'top-left') {
          launchX = -24;
          launchY = -24;
        } else if (source === 'top-right') {
          launchX = width + 24;
          launchY = -24;
        } else if (source === 'bottom-left') {
          launchX = -24;
          launchY = height + 24;
        } else if (source === 'bottom-right') {
          launchX = width + 24;
          launchY = height + 24;
        }

        const targetX = width * (0.13 + Math.random() * 0.74);
        const targetY = height * (0.1 + Math.random() * 0.54);
        const travelTime = (finale ? 390 + Math.random() * 120 : 500 + Math.random() * 250) / Math.max(0.78, Math.min(1.18, scale));
        rockets.push({
          x: launchX,
          y: launchY,
          previousX: launchX,
          previousY: launchY,
          startX: launchX,
          startY: launchY,
          targetX,
          targetY,
          elapsed: 0,
          travelTime,
          color: colors[Math.floor(Math.random() * colors.length)],
          scale,
          finale
        });
      };

      const explode = (rocket) => {
        const burstScale = rocket.finale ? Math.max(1.35, rocket.scale) : rocket.scale;
        const count = Math.min(Math.round(particleCount * burstScale), maximumParticles - particles.length);
        const ringOffset = Math.random() * Math.PI * 2;
        for (let index = 0; index < count; index += 1) {
          const angle = ringOffset + (Math.PI * 2 * index) / count + (Math.random() - 0.5) * 0.16;
          const speed = ((rocket.finale ? 145 : 120) + Math.random() * (rocket.finale ? 225 : 180)) * Math.sqrt(burstScale);
          particles.push({
            x: rocket.targetX,
            y: rocket.targetY,
            previousX: rocket.targetX,
            previousY: rocket.targetY,
            velocityX: Math.cos(angle) * speed,
            velocityY: Math.sin(angle) * speed,
            age: 0,
            life: ((rocket.finale ? 1250 : 1050) + Math.random() * 900) * Math.max(0.78, Math.min(1.22, burstScale)),
            color: index % 5 === 0 ? '#ffffff' : rocket.color,
            size: (1.1 + Math.random() * 2) * Math.max(0.78, Math.min(1.25, burstScale)),
            twinkle: Math.random() * Math.PI * 2
          });
        }
      };

      const drawFrame = (now) => {
        const elapsed = now - startedAt;
        const delta = Math.min(42, now - lastFrame);
        lastFrame = now;
        context.clearRect(0, 0, width, height);

        while (launchIndex < launchTimes.length && elapsed >= launchTimes[launchIndex]) {
          const finale = launchIndex >= launchTimes.length - (mobile ? 3 : 5);
          const launchCount = finale && !mobile ? 2 : 1;
          for (let index = 0; index < launchCount; index += 1) {
            const patternIndex = launchIndex + index * 4;
            const source = launchSources[patternIndex % launchSources.length];
            const scale = finale ? 1.35 + (patternIndex % 3) * 0.18 : launchScales[patternIndex % launchScales.length];
            launchRocket(source, scale, finale);
          }
          launchIndex += 1;
        }

        for (let index = rockets.length - 1; index >= 0; index -= 1) {
          const rocket = rockets[index];
          rocket.elapsed += delta;
          const progress = Math.min(1, rocket.elapsed / rocket.travelTime);
          const eased = 1 - Math.pow(1 - progress, 2);
          rocket.previousX = rocket.x;
          rocket.previousY = rocket.y;
          rocket.x = rocket.startX + (rocket.targetX - rocket.startX) * eased;
          rocket.y = rocket.startY + (rocket.targetY - rocket.startY) * eased;

          context.save();
          context.globalAlpha = Math.min(1, progress * 5);
          context.strokeStyle = rocket.color;
          context.lineWidth = (rocket.finale ? 2.8 : 2) * Math.max(0.72, Math.min(1.4, rocket.scale));
          context.lineCap = 'round';
          context.shadowColor = rocket.color;
          context.shadowBlur = 15;
          context.beginPath();
          const trailX = rocket.previousX + (rocket.startX - rocket.targetX) * 0.018;
          const trailY = rocket.previousY + (rocket.startY - rocket.targetY) * 0.018;
          context.moveTo(trailX, trailY);
          context.lineTo(rocket.x, rocket.y);
          context.stroke();
          context.fillStyle = '#ffffff';
          context.beginPath();
          context.arc(rocket.x, rocket.y, (rocket.finale ? 3.2 : 2.5) * Math.max(0.72, Math.min(1.35, rocket.scale)), 0, Math.PI * 2);
          context.fill();
          context.restore();

          if (progress >= 1) {
            explode(rocket);
            rockets.splice(index, 1);
          }
        }

        for (let index = particles.length - 1; index >= 0; index -= 1) {
          const particle = particles[index];
          particle.age += delta;
          if (particle.age >= particle.life) {
            particles.splice(index, 1);
            continue;
          }

          particle.previousX = particle.x;
          particle.previousY = particle.y;
          particle.velocityX *= Math.pow(0.982, delta / 16.67);
          particle.velocityY = particle.velocityY * Math.pow(0.982, delta / 16.67) + 92 * (delta / 1000);
          particle.x += particle.velocityX * (delta / 1000);
          particle.y += particle.velocityY * (delta / 1000);
          const remaining = 1 - particle.age / particle.life;
          const flicker = 0.7 + Math.sin(now / 52 + particle.twinkle) * 0.3;

          context.save();
          context.globalAlpha = Math.max(0, remaining) * flicker;
          context.strokeStyle = particle.color;
          context.lineWidth = particle.size;
          context.lineCap = 'round';
          context.shadowColor = particle.color;
          context.shadowBlur = 12;
          context.beginPath();
          context.moveTo(particle.previousX, particle.previousY);
          context.lineTo(particle.x, particle.y);
          context.stroke();
          context.restore();
        }

        if (elapsed < duration && this.hypeFx) {
          this.hypeFxRaf = window.requestAnimationFrame(drawFrame);
        }
      };

      resize();
      this.hypeFxResize = resize;
      window.addEventListener('resize', resize, { passive: true });
      this.hypeFxRaf = window.requestAnimationFrame(drawFrame);
    }

    playHypeSound(reduceMotion, experienceGeneration) {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;

      try {
        this.audioContext = this.audioContext || new AudioContext();
        const schedule = () => {
          if (experienceGeneration !== this.hypeFxGeneration || !this.hypeFx) return;
          this.scheduleHypeSound(reduceMotion, experienceGeneration);
        };

        if (this.audioContext.state === 'suspended') {
          this.audioContext.resume().then(schedule).catch(() => {});
        } else {
          schedule();
        }
      } catch (error) {
        // Sound is enhancement-only and never blocks Hype.
      }
    }

    scheduleHypeSound(reduceMotion, experienceGeneration) {
      const context = this.audioContext;
      if (!context || experienceGeneration !== this.hypeFxGeneration || !this.hypeFx) return;

      this.stopHypeSound();

      const duration = reduceMotion ? 1.35 : 10.2;
      const start = context.currentTime + 0.015;
      const compressor = context.createDynamicsCompressor();
      const master = context.createGain();
      compressor.threshold.value = -22;
      compressor.knee.value = 18;
      compressor.ratio.value = 6;
      compressor.attack.value = 0.005;
      compressor.release.value = 0.24;
      master.gain.setValueAtTime(0.0001, start);
      master.gain.exponentialRampToValueAtTime(0.13, start + 0.06);
      master.gain.setValueAtTime(0.13, start + Math.max(0.1, duration - 0.85));
      master.gain.exponentialRampToValueAtTime(0.0001, start + duration);
      master.connect(compressor);
      compressor.connect(context.destination);
      this.hypeAudioNodes.push(master, compressor);

      const createWhoosh = (offset, whooshDuration, gainValue) => {
        const frameCount = Math.max(1, Math.floor(context.sampleRate * whooshDuration));
        const buffer = context.createBuffer(1, frameCount, context.sampleRate);
        const data = buffer.getChannelData(0);
        for (let index = 0; index < frameCount; index += 1) {
          const progress = index / frameCount;
          data[index] = (Math.random() * 2 - 1) * Math.sin(Math.PI * progress) * (1 - progress * 0.42);
        }

        const source = context.createBufferSource();
        const filter = context.createBiquadFilter();
        const gain = context.createGain();
        source.buffer = buffer;
        filter.type = 'bandpass';
        filter.Q.value = 0.72;
        filter.frequency.setValueAtTime(520, start + offset);
        filter.frequency.exponentialRampToValueAtTime(3600, start + offset + whooshDuration * 0.68);
        gain.gain.setValueAtTime(0.0001, start + offset);
        gain.gain.exponentialRampToValueAtTime(gainValue, start + offset + whooshDuration * 0.22);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + whooshDuration);
        source.connect(filter);
        filter.connect(gain);
        gain.connect(master);
        source.start(start + offset);
        source.stop(start + offset + whooshDuration + 0.05);
        this.hypeAudioNodes.push(source, filter, gain);
      };

      const createChime = (frequency, offset, chimeDuration, gainValue, panValue) => {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(frequency, start + offset);
        oscillator.frequency.exponentialRampToValueAtTime(frequency * 1.006, start + offset + chimeDuration);
        gain.gain.setValueAtTime(0.0001, start + offset);
        gain.gain.exponentialRampToValueAtTime(gainValue, start + offset + 0.025);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + chimeDuration);
        oscillator.connect(gain);

        if (typeof context.createStereoPanner === 'function') {
          const panner = context.createStereoPanner();
          panner.pan.value = panValue;
          gain.connect(panner);
          panner.connect(master);
          this.hypeAudioNodes.push(panner);
        } else {
          gain.connect(master);
        }

        oscillator.start(start + offset);
        oscillator.stop(start + offset + chimeDuration + 0.05);
        this.hypeAudioNodes.push(oscillator, gain);
      };

      const createBoom = (offset, gainValue, panValue) => {
        const boomDuration = 1.05;
        const frameCount = Math.max(1, Math.floor(context.sampleRate * boomDuration));
        const buffer = context.createBuffer(1, frameCount, context.sampleRate);
        const data = buffer.getChannelData(0);
        for (let index = 0; index < frameCount; index += 1) {
          const progress = index / frameCount;
          data[index] = (Math.random() * 2 - 1) * Math.pow(1 - progress, 3.1);
        }

        const source = context.createBufferSource();
        const filter = context.createBiquadFilter();
        const gain = context.createGain();
        source.buffer = buffer;
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(720, start + offset);
        filter.frequency.exponentialRampToValueAtTime(120, start + offset + boomDuration);
        gain.gain.setValueAtTime(gainValue, start + offset);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + boomDuration);
        source.connect(filter);
        filter.connect(gain);

        if (typeof context.createStereoPanner === 'function') {
          const panner = context.createStereoPanner();
          panner.pan.value = panValue;
          gain.connect(panner);
          panner.connect(master);
          this.hypeAudioNodes.push(panner);
        } else {
          gain.connect(master);
        }

        source.start(start + offset);
        source.stop(start + offset + boomDuration + 0.05);
        this.hypeAudioNodes.push(source, filter, gain);
      };

      const createCrackle = (offset, crackleDuration, gainValue, panValue) => {
        const pulseCount = 7;
        for (let index = 0; index < pulseCount; index += 1) {
          const pulseOffset = offset + 0.05 + Math.random() * crackleDuration;
          const oscillator = context.createOscillator();
          const gain = context.createGain();
          oscillator.type = index % 2 ? 'triangle' : 'square';
          oscillator.frequency.value = 1250 + Math.random() * 2800;
          gain.gain.setValueAtTime(0.0001, start + pulseOffset);
          gain.gain.exponentialRampToValueAtTime(gainValue * (0.55 + Math.random() * 0.45), start + pulseOffset + 0.008);
          gain.gain.exponentialRampToValueAtTime(0.0001, start + pulseOffset + 0.06 + Math.random() * 0.08);
          oscillator.connect(gain);

          if (typeof context.createStereoPanner === 'function') {
            const panner = context.createStereoPanner();
            panner.pan.value = Math.max(-1, Math.min(1, panValue + (Math.random() - 0.5) * 0.3));
            gain.connect(panner);
            panner.connect(master);
            this.hypeAudioNodes.push(panner);
          } else {
            gain.connect(master);
          }

          oscillator.start(start + pulseOffset);
          oscillator.stop(start + pulseOffset + 0.18);
          this.hypeAudioNodes.push(oscillator, gain);
        }
      };

      if (reduceMotion) {
        createWhoosh(0, 0.34, 0.12);
        createBoom(0.28, 0.32, 0);
        createChime(293.66, 0.08, 0.82, 0.22, -0.18);
        createChime(440, 0.23, 0.92, 0.16, 0.2);
      } else {
        const audioMobile = window.matchMedia('(max-width: 639px)').matches;
        const fireworkOffsets = audioMobile
          ? [0.62, 1.18, 1.78, 2.4, 3.04, 3.7, 4.38, 5.04, 5.72, 6.4, 7.04, 7.62, 8.12, 8.54, 8.9, 9.22]
          : [0.52, 0.88, 1.28, 1.7, 2.14, 2.6, 3.08, 3.56, 4.06, 4.56, 5.06, 5.56, 6.06, 6.56, 7.04, 7.5, 7.92, 8.3, 8.64, 8.94, 9.22, 9.48];
        const fireworkScales = [0.68, 1, 1.42, 0.78, 1.18, 0.62, 1.5, 0.88, 1.28, 0.72, 1.08];
        fireworkOffsets.forEach((offset, index) => {
          const pan = ((index % 5) - 2) * 0.22;
          const scale = index >= fireworkOffsets.length - 4 ? 1.55 : fireworkScales[index % fireworkScales.length];
          createWhoosh(Math.max(0, offset - 0.38), 0.3 + scale * 0.12, 0.035 + scale * 0.032);
          createBoom(offset, 0.13 + scale * 0.13, pan);
          createCrackle(offset, 0.2 + scale * 0.18, 0.012 + scale * 0.012, pan);
        });
        createChime(523.25, 0.12, 1.15, 0.075, -0.22);
        createChime(659.25, 0.28, 1.35, 0.065, 0.2);
        createChime(783.99, 0.48, 1.55, 0.055, 0);
        createChime(1046.5, 8.72, 1.35, 0.065, 0);
      }

      this.hypeAudioTimer = window.setTimeout(() => this.stopHypeSound(), (duration + 0.2) * 1000);
    }

    stopHypeSound() {
      if (this.hypeAudioTimer) {
        clearTimeout(this.hypeAudioTimer);
        this.hypeAudioTimer = null;
      }

      this.hypeAudioNodes.forEach((node) => {
        try {
          if (typeof node.stop === 'function') node.stop();
        } catch (error) {
          // A source may have already stopped on its own.
        }

        try {
          node.disconnect();
        } catch (error) {
          // Some browsers throw when an already-disconnected node is cleaned up.
        }
      });
      this.hypeAudioNodes = [];
    }

    stopHypeExperience() {
      this.hypeFxGeneration += 1;
      if (this.hypeFxTimer) {
        clearTimeout(this.hypeFxTimer);
        this.hypeFxTimer = null;
      }
      if (this.hypeFxRaf) {
        window.cancelAnimationFrame(this.hypeFxRaf);
        this.hypeFxRaf = null;
      }
      if (this.hypeFxResize) {
        window.removeEventListener('resize', this.hypeFxResize);
        this.hypeFxResize = null;
      }

      if (this.hypeFx) {
        try {
          if (this.hypeFx.matches(':popover-open')) this.hypeFx.hidePopover();
        } catch (error) {
          // Popover selectors are unsupported in older browsers.
        }
        this.hypeFx.remove();
        this.hypeFx = null;
      }
      this.modal?.classList.remove('bpv-modal--hype-fx');

      this.stopHypeSound();
    }

    publishEngagement(eventName, detail) {
      const payload = {
        ...detail,
        section_id: this.root.dataset.sectionId || '',
        page_path: window.location.pathname
      };

      document.dispatchEvent(new CustomEvent(eventName, { detail: payload }));

      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: eventName, ...payload });
      }

      try {
        const published = window.Shopify?.analytics?.publish?.(eventName, payload);
        if (published && typeof published.catch === 'function') published.catch(() => {});
      } catch (error) {
        // Analytics must never block the shopper interaction.
      }
    }

    setHypePending(key, pending) {
      this.root.querySelectorAll('[data-bpv-hype]').forEach((button) => {
        if (button.dataset.hypeKey !== key) return;
        if (pending) {
          button.setAttribute('aria-busy', 'true');
        } else {
          button.removeAttribute('aria-busy');
        }
      });
    }

    async toggleHype(button) {
      const key = button.dataset.hypeKey;
      if (!key || this.pendingHype.has(key)) return;

      const generation = this.bumpEngagementGeneration(key);
      const canSyncSharedCount = Boolean(this.engagementEndpoint && this.isValidEngagementKey(key));
      const active = !this.isHyped(key);
      this.setHyped(key, active);
      this.syncHypeButtons(key);
      this.announceHype(button, active);

      if (active) {
        this.celebrateHype(button, key);
      } else {
        this.stopHypeExperience();
      }

      this.publishEngagement('balour_drop_hype', {
        item_key: key,
        item_name: button.dataset.hypeName || '',
        action: active ? 'add' : 'remove',
        status: canSyncSharedCount ? 'attempted' : 'device_saved'
      });

      if (!canSyncSharedCount) return;

      this.pendingHype.add(key);
      this.setHypePending(key, true);

      try {
        const payload = await this.requestEngagement('hype', {
          method: 'POST',
          body: {
            item_key: key,
            item_name: button.dataset.hypeName || '',
            visitor_token: this.visitorToken,
            event_id: this.createEventId(),
            hyped: active
          }
        });
        const applied = this.applyCountsPayload(
          payload,
          key,
          true,
          new Map([[key, generation]])
        );
        if (!applied.has(key) || !Number.isFinite(this.sharedCounts.get(key)?.hypes)) {
          await this.loadSharedCounts([key]);
        }
        this.publishEngagement('balour_drop_hype_confirmed', {
          item_key: key,
          item_name: button.dataset.hypeName || '',
          action: active ? 'add' : 'remove'
        });
      } catch (error) {
        if (this.destroyed) return;
        this.setHyped(key, !active);
        this.syncHypeButtons(key);
        const status = this.root.querySelector('[data-bpv-hype-announcer]');
        if (status) status.textContent = 'Hype could not be saved. Please try again.';
        if (active) this.stopHypeExperience();
        this.publishEngagement('balour_drop_hype_failed', {
          item_key: key,
          item_name: button.dataset.hypeName || '',
          action: active ? 'add' : 'remove'
        });
      } finally {
        this.pendingHype.delete(key);
        if (!this.destroyed) this.setHypePending(key, false);
      }
    }

    recordPreviewView(trigger) {
      const key = trigger.dataset.bpvEngagementKey;
      if (!key) return;

      const storageKey = `${viewStoragePrefix}${key}`;
      const stored = Number(this.getStoredValue(storageKey));
      const remembered = this.memoryViews.get(key) || 0;
      const lastViewed = Number.isFinite(stored) && stored > 0 ? stored : remembered;
      const now = Date.now();

      if (lastViewed && now - lastViewed < viewWindow) return;

      const generation = this.bumpEngagementGeneration(key);
      const canSyncSharedCount = Boolean(this.engagementEndpoint && this.isValidEngagementKey(key));
      this.memoryViews.set(key, now);
      if (!canSyncSharedCount) this.setStoredValue(storageKey, String(now));
      this.publishEngagement('balour_drop_preview_view', {
        item_key: key,
        item_name: trigger.dataset.bpvEngagementName || '',
        dedupe_window_hours: 24
      });

      if (canSyncSharedCount) {
        this.requestEngagement('view', {
          method: 'POST',
          body: {
            item_key: key,
            item_name: trigger.dataset.bpvEngagementName || '',
            visitor_token: this.visitorToken,
            event_id: this.createEventId(),
            dedupe_window_hours: 24
          }
        })
          .then(async (payload) => {
            this.setStoredValue(storageKey, String(now));
            const applied = this.applyCountsPayload(
              payload,
              key,
              false,
              new Map([[key, generation]])
            );
            if (!applied.has(key) || !Number.isFinite(this.sharedCounts.get(key)?.views)) {
              await this.loadSharedCounts([key]);
            }
          })
          .catch(() => {});
      }
    }

    handleStorageChange(event) {
      if (!event.key || !event.key.startsWith(hypeStoragePrefix)) return;
      this.syncHypeButtons(event.key.slice(hypeStoragePrefix.length));
    }

    handleRootClick(event) {
      const hypeButton = event.target.closest('[data-bpv-hype]');
      if (hypeButton && this.root.contains(hypeButton)) {
        event.preventDefault();
        this.toggleHype(hypeButton);
        return;
      }

      const trigger = event.target.closest('[data-bpv-open]');
      if (trigger && this.root.contains(trigger)) {
        this.openDossier(trigger);
        return;
      }

      const closeButton = event.target.closest('[data-bpv-close]');
      if (closeButton && this.root.contains(closeButton)) this.closeDossier();
    }

    handleModalClick(event) {
      if (event.target === this.modal) this.closeDossier();
    }

    handleModalKeydown(event) {
      if (!this.modal?.classList.contains('is-fallback')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDossier();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = [...this.modal.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter((element) => !element.hidden && element.getClientRects().length > 0);

      if (!focusable.length) {
        event.preventDefault();
        this.modal.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    isModalOpen() {
      return Boolean(this.modal && (this.modal.open || this.modal.hasAttribute('open')));
    }

    setFallbackIsolation(active) {
      if (!active) {
        this.fallbackIsolation.forEach(({ element, inert, ariaHidden }) => {
          if ('inert' in element) element.inert = inert;

          if (ariaHidden === null) {
            element.removeAttribute('aria-hidden');
          } else {
            element.setAttribute('aria-hidden', ariaHidden);
          }
        });

        this.fallbackIsolation = [];
        return;
      }

      this.fallbackIsolation = [];
      let branch = this.modal;

      while (branch?.parentElement) {
        const parent = branch.parentElement;

        [...parent.children].forEach((sibling) => {
          if (sibling === branch || sibling.contains(this.modal)) return;

          this.fallbackIsolation.push({
            element: sibling,
            inert: 'inert' in sibling ? sibling.inert : false,
            ariaHidden: sibling.getAttribute('aria-hidden')
          });

          if ('inert' in sibling) {
            sibling.inert = true;
          } else {
            sibling.setAttribute('aria-hidden', 'true');
          }
        });

        branch = parent;
        if (parent === document.body) break;
      }
    }

    openDossier(trigger) {
      if (!this.modal || !this.mount) return;

      const templateId = trigger.dataset.bpvTemplate;
      const template = templateId ? document.getElementById(templateId) : null;
      if (!(template instanceof HTMLTemplateElement)) return;

      this.mount.replaceChildren(template.content.cloneNode(true));
      this.refreshCountdowns();
      this.syncHypeButtons();
      this.syncEngagementCounts();
      this.activeTrigger = trigger;

      const title = this.mount.querySelector('h2[id]');
      if (title) {
        this.modal.setAttribute('aria-labelledby', title.id);
        this.modal.removeAttribute('aria-label');
      }

      if (!this.isModalOpen()) {
        if (typeof this.modal.showModal === 'function') {
          this.modal.showModal();
        } else {
          this.modal.setAttribute('open', '');
          this.modal.classList.add('is-fallback');
          this.setFallbackIsolation(true);
        }
      }

      updateBodyLock();
      this.recordPreviewView(trigger);

      requestAnimationFrame(() => {
        const closeButton = this.modal.querySelector('[data-bpv-close]');
        if (closeButton) closeButton.focus();
      });
    }

    closeDossier() {
      if (!this.isModalOpen()) return;

      if (typeof this.modal.close === 'function' && !this.modal.classList.contains('is-fallback')) {
        this.modal.close();
      } else {
        this.modal.removeAttribute('open');
        this.modal.classList.remove('is-fallback');
        this.handleModalClose();
      }
    }

    handleModalClose() {
      if (this.hypeFx && this.hypeFx.closest('[data-bpv-modal]')) this.stopHypeExperience();
      this.setFallbackIsolation(false);
      updateBodyLock();

      if (this.activeTrigger && document.contains(this.activeTrigger)) {
        this.activeTrigger.focus({ preventScroll: true });
      }

      this.activeTrigger = null;
    }

    openBlock(blockId) {
      const card = [...this.root.querySelectorAll('[data-bpv-block]')].find(
        (item) => item.dataset.bpvBlock === blockId
      );

      if (!card) return;
      card.classList.add('is-visible');

      const trigger = card.querySelector('[data-bpv-open]');
      if (trigger) this.openDossier(trigger);
    }

    destroy() {
      this.destroyed = true;
      this.stopHypeExperience();
      this.engagementRequests.forEach((controller) => controller.abort());
      this.engagementRequests.clear();
      if (this.audioContext) {
        const closePromise = this.audioContext.close?.();
        if (closePromise && typeof closePromise.catch === 'function') closePromise.catch(() => {});
        this.audioContext = null;
      }

      if (this.isModalOpen()) {
        if (typeof this.modal.close === 'function' && !this.modal.classList.contains('is-fallback')) {
          this.modal.close();
        } else {
          this.modal.removeAttribute('open');
          this.modal.classList.remove('is-fallback');
        }

        this.handleModalClose();
      }

      this.root.removeEventListener('click', this.handleRootClick);
      window.removeEventListener('storage', this.handleStorageChange);

      if (this.modal) {
        this.modal.removeEventListener('click', this.handleModalClick);
        this.modal.removeEventListener('close', this.handleModalClose);
        this.modal.removeEventListener('keydown', this.handleModalKeydown);
      }

      if (this.observer) this.observer.disconnect();
      if (this.countdownTimer) clearInterval(this.countdownTimer);
      delete this.root.bpvInstance;
    }
  }

  const getRoots = (scope) => {
    const roots = [];
    if (scope instanceof Element && scope.matches(rootSelector)) roots.push(scope);
    roots.push(...scope.querySelectorAll(rootSelector));
    return roots;
  };

  const initialize = (scope = document) => {
    getRoots(scope).forEach((root) => {
      if (!root.bpvInstance) root.bpvInstance = new BalourPreviewVault(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    getRoots(event.target).forEach((root) => {
      if (root.bpvInstance) root.bpvInstance.destroy();
    });
  });

  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail && event.detail.blockId;
    if (!blockId) return;

    document.querySelectorAll(rootSelector).forEach((root) => {
      if (root.bpvInstance) root.bpvInstance.openBlock(blockId);
    });
  });

  document.addEventListener('visibilitychange', () => {
    document.querySelectorAll(rootSelector).forEach((root) => {
      if (!root.bpvInstance) return;

      if (document.hidden) {
        root.bpvInstance.stopHypeExperience();
      } else {
        root.bpvInstance.refreshCountdowns();
      }
    });
  });
})();
